import ShaderService from "../shader/shader_service";
import ResourceService from "../resource/resourceservice";
import RenderObject from "./renderobject";
import CollisionService from "../collision/collision_service";
import Camera from "../scene/camera";
import { RENDER_SAMPLE_COUNT, is_multisampled } from "./render_config";
import type ScreenSpaceOutline from "./screen_space_outline";

// Extracted as a pure, exported helper (rather than inlined in render())
// so the count=1 fallback vs. count>1 resolve-wiring branch can be
// unit-tested directly against plain object stand-ins for GPUTextureView,
// without needing a real GPUDevice/canvas/WebGPU context. render() is the
// only caller in production.
export function buildColorAttachment(
  multisampled: boolean,
  msaaColorView: GPUTextureView,
  canvasView: GPUTextureView,
  clear: { r: number; g: number; b: number; a: number }
): GPURenderPassColorAttachment {
  if (multisampled) {
    return {
      view: msaaColorView,
      resolveTarget: canvasView,
      clearValue: { r: clear.r, g: clear.g, b: clear.b, a: clear.a },
      loadOp: "clear",
      storeOp: "discard",
    };
  }

  return {
    view: canvasView,
    clearValue: { r: clear.r, g: clear.g, b: clear.b, a: clear.a },
    loadOp: "clear",
    storeOp: "store",
  };
}

export default class RenderService {
  private shader_service: ShaderService;
  private resource_service: ResourceService;
  private collision_service: CollisionService;
  canvas: HTMLCanvasElement | null = null;
  private context: GPUCanvasContext | null = null;

  private format: GPUTextureFormat = "rgba8unorm";
  private animationFrameId: number | null = null;
  private render_objects: RenderObject[];
  private screenOutlines: ScreenSpaceOutline[] = [];

  set_screen_space_outline(effect: ScreenSpaceOutline | null): void {
    this.set_screen_space_outlines(effect ? [effect] : []);
  }

  set_screen_space_outlines(effects: readonly ScreenSpaceOutline[]): void {
    const next = [...new Set(effects)];
    for (const effect of this.screenOutlines) {
      if (!next.includes(effect)) effect.dispose();
    }
    this.screenOutlines = next;
  }

  private depthTexture: GPUTexture | null = null;
  private depthView: GPUTextureView | null = null;
  private depthFormat: GPUTextureFormat = "depth24plus";

  // Offscreen multisampled color target that the main render pass draws
  // into when RENDER_SAMPLE_COUNT > 1 (see render_config.ts). Every frame's
  // color attachment resolves from this texture into the canvas's current
  // single-sampled texture via `resolveTarget` - see render(). Unused (and
  // never allocated) when RENDER_SAMPLE_COUNT === 1, in which case the
  // render pass draws straight into the canvas's current texture with no
  // resolve target, matching the pre-MSAA behavior exactly.
  private msaaColorTexture: GPUTexture | null = null;
  private msaaColorView: GPUTextureView | null = null;

  // The sample count the currently-allocated MSAA color/depth textures
  // were created with, plus the canvas size/format they match. Cached so
  // resize()/render() only recreate these GPUTexture objects when the
  // canvas size, presentation format, or device actually changed - never
  // once per frame - while still catching every case that does require a
  // fresh allocation (including a device-lifecycle change via reset()).
  private msaaAllocatedWidth = 0;
  private msaaAllocatedHeight = 0;
  private msaaAllocatedFormat: GPUTextureFormat | null = null;
  private msaaAllocatedSampleCount = 0;
  private msaaAllocatedDevice: GPUDevice | null = null;

  private presentationWidth = 1;
  private presentationHeight = 1;
  private camera:Camera| null = null;
  private clear_r:number = 0.0;
  private clear_g:number = 0.0;
  private clear_b:number = 0.0;
  private clear_a:number = 1.0;

  constructor(
    shader_service: ShaderService,
    resource_service: ResourceService,
    collision_service: CollisionService
  ) {
    this.shader_service = shader_service;
    this.resource_service = resource_service;
    this.collision_service = collision_service;
    this.render_objects = [];
  }

  private get device(): GPUDevice | null {
    return this.shader_service?.get_device() ?? null;
  }

  set_camera(camera:Camera)
  {
      this.camera = camera;
  }

  set_clear_color(r:number,g:number,b:number,a:number)
  {
       this.clear_r = r;
       this.clear_g = g;
       this.clear_b = b;
       this.clear_a = a;
  }

  private configureContext() {
    if (!this.device || !this.context) return;

    this.context.configure({
      device: this.device,
      format: this.format,
      alphaMode: "premultiplied",
    });
  }

  private destroyDepthTexture() {
    if (this.depthTexture) {
      this.depthTexture.destroy();
      this.depthTexture = null;
    }
    this.depthView = null;
  }

  private recreateDepthTexture() {
    if (!this.device || !this.canvas) return;
    // Zero-sized canvas safeguard: creating a 0x0 GPUTexture is a WebGPU
    // validation error, and there is nothing useful to render into one
    // anyway - leave any previous depth texture destroyed/null and let
    // render() bail out (its `!this.depthView` guard) until a real resize.
    if (this.canvas.width === 0 || this.canvas.height === 0) {
      this.destroyDepthTexture();
      return;
    }

    this.destroyDepthTexture();

    // Depth attachment sample count always matches the color attachment's
    // (RENDER_SAMPLE_COUNT) - WebGPU requires every attachment in a render
    // pass to agree on sample count, and every pipeline in this pass
    // (static/skinned/outline/papercraft/lines/gizmos/shadows/diorama/
    // reticle/plane/sphere/collision) is built with the same constant
    // (see render_config.ts and each *_renderpipeline.ts).
    this.depthTexture = this.device.createTexture({
      size: {
        width: this.canvas.width,
        height: this.canvas.height,
        depthOrArrayLayers: 1,
      },
      format: this.depthFormat,
      sampleCount: RENDER_SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });

    this.depthView = this.depthTexture.createView();
  }

  private destroyMsaaColorTexture() {
    if (this.msaaColorTexture) {
      this.msaaColorTexture.destroy();
      this.msaaColorTexture = null;
    }
    this.msaaColorView = null;
    this.msaaAllocatedWidth = 0;
    this.msaaAllocatedHeight = 0;
    this.msaaAllocatedFormat = null;
    this.msaaAllocatedSampleCount = 0;
    this.msaaAllocatedDevice = null;
  }

  /**
   * Ensures the offscreen multisampled color texture (used only when
   * RENDER_SAMPLE_COUNT > 1) matches the current device, canvas size, and
   * presentation format. Recreates it exactly when one of those actually
   * changed - resize(), a presentation-format change, or a device reset
   * via reset() - never unconditionally once per frame, so steady-state
   * rendering allocates nothing here. When RENDER_SAMPLE_COUNT === 1 any
   * previously-allocated texture is torn down and this is a no-op,
   * matching the direct-to-canvas single-sampled fallback path.
   */
  private ensureMsaaColorTexture() {
    if (!this.device || !this.canvas) return;

    if (!is_multisampled()) {
      this.destroyMsaaColorTexture();
      return;
    }

    // Zero-sized canvas safeguard, matching recreateDepthTexture().
    if (this.canvas.width === 0 || this.canvas.height === 0) {
      this.destroyMsaaColorTexture();
      return;
    }

    const upToDate =
      this.msaaColorTexture !== null &&
      this.msaaAllocatedWidth === this.canvas.width &&
      this.msaaAllocatedHeight === this.canvas.height &&
      this.msaaAllocatedFormat === this.format &&
      this.msaaAllocatedSampleCount === RENDER_SAMPLE_COUNT &&
      this.msaaAllocatedDevice === this.device;

    if (upToDate) return;

    this.destroyMsaaColorTexture();

    this.msaaColorTexture = this.device.createTexture({
      label: "render_service_msaa_color",
      size: {
        width: this.canvas.width,
        height: this.canvas.height,
        depthOrArrayLayers: 1,
      },
      format: this.format,
      sampleCount: RENDER_SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });

    this.msaaColorView = this.msaaColorTexture.createView();

    this.msaaAllocatedWidth = this.canvas.width;
    this.msaaAllocatedHeight = this.canvas.height;
    this.msaaAllocatedFormat = this.format;
    this.msaaAllocatedSampleCount = RENDER_SAMPLE_COUNT;
    this.msaaAllocatedDevice = this.device;
  }

  reset(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.context = this.canvas.getContext("webgpu") as GPUCanvasContext | null;
    if (!this.context || !this.device) return;

    this.configureContext();
    this.resize();
  }

  add(obj: RenderObject) {
    this.render_objects.push(obj);
  }

  async build():Promise<void> {
    if (!this.device) return;

    await this.collision_service.build(
      this.device,
      this.format,
      this.shader_service,
      this.resource_service
    );
    await Promise.all(this.render_objects.map(obj=>
      obj.build(this.device!,this.format,this.shader_service,this.resource_service)));
  }

  clear() {
    this.set_screen_space_outline(null);
    this.render_objects = [];
  }

  get_format(): GPUTextureFormat {
    return this.format;
  }

  get_context(): GPUCanvasContext | null {
    return this.context;
  }

  get_depth_format(): GPUTextureFormat {
    return this.depthFormat;
  }

  resize(width?: number, height?: number, dpr?: number): boolean {
    if (!this.canvas) return false;

    const rect = this.canvas.getBoundingClientRect();
    const pixelRatio = Math.min(dpr ?? window.devicePixelRatio ?? 1, 2);

    const nextWidth = Math.max(1, Math.round((width ?? rect.width) * pixelRatio));
    const nextHeight = Math.max(1, Math.round((height ?? rect.height) * pixelRatio));

    const resized =
      this.canvas.width !== nextWidth || this.canvas.height !== nextHeight;

    if (!resized) return false;

    this.canvas.width = nextWidth;
    this.canvas.height = nextHeight;
    this.presentationWidth = nextWidth;
    this.presentationHeight = nextHeight;

    this.configureContext();
    this.recreateDepthTexture();
    this.ensureMsaaColorTexture();

    return true;
  }

  start() {
    if (this.animationFrameId === null) {
      this.animationFrameId = requestAnimationFrame(this.frame);
    }
  }

  private frame = (time: number) => {
    this.render(time);
    this.animationFrameId = requestAnimationFrame(this.frame);
  };

  stop() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  render(time: number) {
    if (!this.device || !this.context || !this.canvas) return;

    this.resize();

    if (!this.depthView) {
      this.recreateDepthTexture();
      if (!this.depthView) return;
    }

    // Safety net: normally allocated/updated by resize(), but this also
    // covers the case where sample count, format, or the GPUDevice itself
    // changed without an explicit resize (e.g. device-lifecycle recovery
    // in reset()) - and it is a fast no-op whenever nothing changed, so it
    // never allocates a new texture on a steady-state frame.
    this.ensureMsaaColorTexture();
    const multisampled = is_multisampled();
    if (multisampled && !this.msaaColorView) return;

    const encoder = this.device.createCommandEncoder();

    // --- Compute phase --------------------------------------------------
    const compute_pass = encoder.beginComputePass({
      label: "render_service_compute_pass",
    });
    for (const obj of this.render_objects) {
      obj.compute(this.device, compute_pass,encoder);
    }
    compute_pass.end();

    // --- Render phase ---------------------------------------------------
    for (const obj of this.render_objects)
    {
      if(this.camera)
      {
        obj.prepare(this.device,this.camera);
      }
    }

    const canvasView = this.context.getCurrentTexture().createView();
    const outlines = this.screenOutlines.filter(effect => effect.active &&
      this.render_objects.includes(effect.afterObject) &&
      (effect.afterObject.layer === 0 || effect.afterObject.layer === 1));
    // All reviewed actors must have written depth before either overlay is drawn.
    // Use actual layer traversal order, not effect registration order.
    const outlinedObjects = new Set(outlines.map(effect => effect.afterObject));
    let afterOutlinedActors: RenderObject | undefined;
    for (const layer of [0, 1])
      for (const obj of this.render_objects)
        if (obj.layer === layer && outlinedObjects.has(obj)) afterOutlinedActors = obj;
    const colorView = multisampled ? this.msaaColorView! : canvasView;

    // Sample count 1 fallback: draw straight into the canvas's current
    // texture with no resolve target (identical to pre-MSAA behavior).
    // Sample count > 1: draw into the offscreen multisampled color
    // texture and resolve into the canvas's current texture via
    // `resolveTarget` - the single-sampled canvas texture is written only
    // by the resolve step, never drawn into directly. See
    // buildColorAttachment() above for the (separately unit-tested) logic.
    const colorAttachment = buildColorAttachment(
      multisampled,
      this.msaaColorView as GPUTextureView,
      canvasView,
      { r: this.clear_r, g: this.clear_g, b: this.clear_b, a: this.clear_a }
    );

    // Preserve multisample color across the mask/overlay interruption. Only
    // the continuation resolves to canvas; the disabled path stays unchanged.
    if (outlines.length) {
      colorAttachment.storeOp = "store";
      delete colorAttachment.resolveTarget;
    }
    let render_pass = encoder.beginRenderPass({
      colorAttachments: [colorAttachment],
      depthStencilAttachment: {
        view: this.depthView,
        depthClearValue: 1.0,
        depthLoadOp: "clear",
        depthStoreOp: "store",
      },
    });

    for (const layer of [0, 1]) {
      for (const obj of this.render_objects) {
        if (obj.layer !== layer) continue;
        obj.draw(render_pass);
        if (afterOutlinedActors === obj) {
          render_pass.end();
          for (const outline of outlines)
            outline.encode(this.device, encoder, this.shader_service, this.format,
              colorView, this.depthView, this.canvas.width, this.canvas.height,
              this.canvas.width / Math.max(1, this.canvas.getBoundingClientRect().width));
          render_pass = encoder.beginRenderPass({
            label: "scene_after_silhouette",
            colorAttachments: [{
              view: colorView,
              ...(multisampled ? { resolveTarget: canvasView } : {}),
              loadOp: "load", storeOp: multisampled ? "discard" : "store",
            }],
            depthStencilAttachment: {
              view: this.depthView, depthLoadOp: "load", depthStoreOp: "store",
            },
          });
        }
      }
    }



    this.collision_service.draw(render_pass);

    render_pass.end();

    // --- Readback encode phase (before submit) --------------------------
    for (const obj of this.render_objects) {
      obj.readback_encode(this.device, encoder);
    }

    // --- Submit ---------------------------------------------------------
    this.device.queue.submit([encoder.finish()]);

    // --- Post-submit cleanup --------------------------------------------
    for (const obj of this.render_objects) {
      obj.finish_draw();
    }

    // --- Readback poll (drives mapAsync) --------------------------------
    for (const obj of this.render_objects) 
    {
          obj.readback_poll(this.device);
    }
  }
}
